"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.COST_CODES = void 0;
exports.opportunityLines = opportunityLines;
exports.acceptedTotals = acceptedTotals;
exports.COST_CODES = {
    normalTime: '001L',
    subcontract: '031S',
    overtime: '011O',
    paint: '021M',
    hire: '041H',
    prelims: '051P',
};
const PERCENT = 100;
const HOURS_PLACES = 100;
const total = (lines, field) => lines.reduce((sum, l) => sum + l[field], 0);
const byCategory = (option, ...categories) => option.breakdown.filter((l) => categories.includes(l.category));
const hours = (value) => Math.round(value * HOURS_PLACES) / HOURS_PLACES;
/** The labour lines: normal time (with foreman, abseil, allowance) and overtime at full cost. */
function labourDrafts(option, rates, labourType) {
    const allHours = byCategory(option, 'labour')[0]?.quantity ?? 0;
    const overtimeHours = byCategory(option, 'overtime')[0]?.quantity ?? 0;
    const loadings = byCategory(option, 'foreman', 'abseil', 'allowance');
    const normalHours = allHours - overtimeHours;
    const normal = {
        productNumber: labourType === 'Subbie' ? exports.COST_CODES.subcontract : exports.COST_CODES.normalTime,
        sell: normalHours * rates.labour.sell + total(loadings, 'sell'),
        cost: normalHours * rates.labour.cost + total(loadings, 'cost'),
        hoursLitres: hours(normalHours),
    };
    if (overtimeHours <= 0)
        return [normal];
    const overtime = {
        productNumber: exports.COST_CODES.overtime,
        sell: overtimeHours * (rates.labour.sell + rates.overtime.sell),
        cost: overtimeHours * (rates.labour.cost + rates.overtime.cost),
        hoursLitres: hours(overtimeHours),
    };
    return [normal, overtime];
}
function otherDrafts(option) {
    const paint = byCategory(option, 'paint', 'consumables');
    const litres = byCategory(option, 'paint').reduce((sum, l) => sum + (l.quantity ?? 0), 0);
    const hire = byCategory(option, 'hire');
    const prelims = byCategory(option, 'otherTrades', 'fuelLevy');
    return [
        { productNumber: exports.COST_CODES.paint, sell: total(paint, 'sell'), cost: total(paint, 'cost'), hoursLitres: hours(litres) },
        { productNumber: exports.COST_CODES.hire, sell: total(hire, 'sell'), cost: total(hire, 'cost'), hoursLitres: null },
        { productNumber: exports.COST_CODES.prelims, sell: total(prelims, 'sell'), cost: total(prelims, 'cost'), hoursLitres: null },
    ];
}
/**
 * The negotiating factor adds sell with no cost (the Summary's Contract
 * Adjustments). Spread over the lines in proportion to their sell, so every
 * line's margin moves the same way and the lines add up to the option's sell.
 */
function withAdjustment(drafts, adjustment) {
    const sell = drafts.reduce((sum, d) => sum + d.sell, 0);
    if (!adjustment || sell <= 0)
        return drafts;
    return drafts.map((d) => ({ ...d, sell: d.sell + (adjustment * d.sell) / sell }));
}
const percentOf = (part, whole) => (whole > 0 ? Math.round((part / whole) * PERCENT * PERCENT) / PERCENT : 0);
/** The option's product lines, in whole dollars; lines with nothing on them are left out. */
function opportunityLines(option, rates, labourType) {
    const drafts = withAdjustment([...labourDrafts(option, rates, labourType), ...otherDrafts(option)], option.contractAdjustment);
    return drafts
        .map((d) => ({ ...d, sell: Math.round(d.sell), cost: Math.round(d.cost) }))
        .filter((d) => d.sell !== 0 || d.cost !== 0)
        .map((d, i) => ({
        productNumber: d.productNumber,
        sequence: i + 1,
        sell: d.sell,
        cost: d.cost,
        hoursLitres: d.hoursLitres,
        marginDollar: d.sell - d.cost,
        marginPercent: percentOf(d.sell - d.cost, d.sell),
    }));
}
/** The opportunity header the lines add up to (hig_totalcost, hig_gp, hig_gpamount). */
function acceptedTotals(lines) {
    const sell = lines.reduce((sum, l) => sum + l.sell, 0);
    const cost = lines.reduce((sum, l) => sum + l.cost, 0);
    return { sell, cost, gpAmount: sell - cost, gpPercent: percentOf(sell - cost, sell) };
}
